
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { saveRedirectPath } from '@/utils/auth-redirect';

interface ProtectedRouteProps {
  element: JSX.Element;
  requiredRole: 'client' | 'practitioner' | 'admin' | 'host' | null;
}

/**
 * Simplified ProtectedRoute — relies entirely on AuthContext for role state.
 * No more DB queries here; all role detection happens in AuthProvider.
 */
const ProtectedRoute = ({ element, requiredRole }: ProtectedRouteProps) => {
  const { user, authStatus, roleStatus, activeRole } = useAuth();

  // Check password recovery context
  const recoveryContext = (() => {
    try {
      const raw = sessionStorage.getItem('password_recovery_in_progress');
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { ts?: number; userType?: string };
      if (!parsed.ts || Date.now() - parsed.ts >= 10 * 60 * 1000) return null;
      return {
        userType: parsed.userType === 'practitioner' ? 'practitioner' : 'client',
      } as const;
    } catch {
      return null;
    }
  })();

  if (recoveryContext) {
    return <Navigate to={`/reset-password?type=${recoveryContext.userType}`} replace />;
  }

  // 1. Still loading auth or role — show spinner
  if (authStatus === 'loading' || roleStatus === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  // 2. Not logged in — redirect to appropriate auth page
  if (!user || authStatus === 'loggedOut') {
    saveRedirectPath();

    let redirectPath = '/';
    if (requiredRole === 'client') redirectPath = '/client-auth';
    else if (requiredRole === 'practitioner') redirectPath = '/practitioner-auth';
    else if (requiredRole === 'admin') redirectPath = '/admin';
    else if (requiredRole === 'host') redirectPath = '/space/auth';

    console.log(`ProtectedRoute: Unauthenticated, redirecting to ${redirectPath}`);
    return <Navigate to={redirectPath} replace />;
  }

  // 3. Logged in, check role match
  if (requiredRole && activeRole !== requiredRole) {
    // Admin mismatch
    if (requiredRole === 'admin') {
      return <Navigate to="/admin" replace />;
    }

    // No role at all — send to auth
    if (activeRole === null) {
      const authPath = requiredRole === 'practitioner' ? '/practitioner-auth'
        : requiredRole === 'host' ? '/space/auth'
        : '/client-auth';
      console.log(`ProtectedRoute: No active role, redirecting to ${authPath}`);
      return <Navigate to={authPath} replace />;
    }

    // Role mismatch — send to their own dashboard
    console.log(`ProtectedRoute: Role mismatch - required: ${requiredRole}, active: ${activeRole}`);
    let dashboardPath = '/client-dashboard';
    if (activeRole === 'practitioner') dashboardPath = '/practitioner-dashboard';
    else if (activeRole === 'admin') dashboardPath = '/admin/dashboard';
    else if (activeRole === 'host') dashboardPath = '/space/dashboard';

    return <Navigate to={dashboardPath} replace />;
  }

  // 4. All checks passed — render the protected content
  return element;
};

export default ProtectedRoute;
